import React, { useCallback, useEffect, useMemo, useState } from "react";
import AdminSidebar from "./AdminSidebar";
import { toast } from "../../utils/toast";
import { adminFetch, adminErrorMessage as readError } from "../../api/adminFetch";
import "../styles/AdminPortfolioManager.css";

// 포트폴리오 관리 — 등록 · 수정 · 삭제 · 노출 순서 변경.
//
// 사례를 늘릴 때마다 배포하지 않으려고 DB 로 옮긴 화면이다.
// 쓰기 API 는 ADMIN 등급만 통과하므로 모든 요청에 토큰을 싣는다.

const EMPTY = {
    title: "",
    seoTitle: "",
    client: "",
    period: "",
    budget: "",
    role: "",
    category: "",
    summary: "",
    problem: "",
    solution: "",
    result: "",
    link: "",
    published: true,
    stack: [],
    images: [],
};

// 분류는 자유 입력이지만, 기존 값을 고를 수 있어야 오타로 필터가 쪼개지지 않는다.
const BASE_CATEGORIES = ["커머스", "마케팅", "데이터", "업무자동화"];

const AdminPortfolioManager = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState("");
    const [editing, setEditing] = useState(null); // null 이면 폼이 닫힌 상태
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await adminFetch("/api/admin/portfolio");
            if (!res.ok) {
                setLoadError(await readError(res));
                setItems([]);
                return;
            }
            setLoadError("");
            setItems(await res.json());
        } catch (err) {
            console.error("포트폴리오 목록 조회 실패", err);
            setLoadError("서버에 연결하지 못했습니다.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const categories = useMemo(() => {
        const used = items.map((p) => p.category).filter(Boolean);
        return Array.from(new Set([...BASE_CATEGORIES, ...used]));
    }, [items]);

    // ------------------------------------------------------------------ 폼

    const startCreate = () => setEditing({ ...EMPTY });
    const startEdit = (item) => setEditing({ ...EMPTY, ...item, stack: item.stack || [], images: item.images || [] });
    const closeForm = () => setEditing(null);

    const setField = (name, value) => setEditing((prev) => ({ ...prev, [name]: value }));
    const onChange = (e) => setField(e.target.name, e.target.value);

    const save = async (e) => {
        e.preventDefault();
        if (!editing.title.trim()) {
            toast("프로젝트명을 입력해 주세요.");
            return;
        }

        setSaving(true);
        try {
            const isNew = !editing.id;
            const res = await adminFetch(
                isNew ? "/api/admin/portfolio" : `/api/admin/portfolio/${editing.id}`,
                {
                    method: isNew ? "POST" : "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(editing),
                }
            );

            if (!res.ok) {
                toast(await readError(res));
                return;
            }
            toast(isNew ? "사례를 등록했습니다." : "사례를 수정했습니다.");
            closeForm();
            load();
        } catch (err) {
            console.error("포트폴리오 저장 실패", err);
            toast("서버 오류가 발생했습니다.");
        } finally {
            setSaving(false);
        }
    };

    // --------------------------------------------------------------- 이미지

    const uploadImages = async (fileList) => {
        const files = Array.from(fileList || []);
        if (files.length === 0) return;

        setUploading(true);
        const added = [];
        let failed = 0;

        for (const file of files) {
            const form = new FormData();
            form.append("file", file);
            try {
                const res = await adminFetch("/api/admin/portfolio/image", {
                    method: "POST",
                    body: form,
                });
                if (!res.ok) {
                    failed += 1;
                    if (res.status === 401 || res.status === 403) {
                        toast(await readError(res));
                        break;
                    }
                    continue;
                }
                const { src } = await res.json();
                // alt 는 파일명에서 확장자만 떼어 기본값으로 둔다. 비워 두면 검색·접근성에서 손해다.
                added.push({ src, alt: file.name.replace(/\.[^.]+$/, "") });
            } catch (err) {
                console.error("이미지 업로드 실패", err);
                failed += 1;
            }
        }

        if (added.length > 0) {
            setEditing((prev) => ({ ...prev, images: [...prev.images, ...added] }));
        }
        if (failed > 0) toast(`${failed}장 업로드에 실패했습니다.`);
        else if (added.length > 0) toast(`${added.length}장 올렸습니다.`);
        setUploading(false);
    };

    const setImageAlt = (idx, alt) =>
        setEditing((prev) => ({
            ...prev,
            images: prev.images.map((img, i) => (i === idx ? { ...img, alt } : img)),
        }));

    const removeImage = (idx) =>
        setEditing((prev) => ({ ...prev, images: prev.images.filter((_, i) => i !== idx) }));

    const moveImage = (idx, dir) =>
        setEditing((prev) => {
            const next = [...prev.images];
            const to = idx + dir;
            if (to < 0 || to >= next.length) return prev;
            [next[idx], next[to]] = [next[to], next[idx]];
            return { ...prev, images: next };
        });

    // ---------------------------------------------------------------- 목록

    const remove = async (item) => {
        try {
            const res = await adminFetch(`/api/admin/portfolio/${item.id}`, {
                method: "DELETE",
            });
            if (!res.ok) {
                toast(await readError(res));
                return;
            }
            toast("삭제했습니다.");
            setConfirmDelete(null);
            if (editing?.id === item.id) closeForm();
            load();
        } catch (err) {
            console.error("포트폴리오 삭제 실패", err);
            toast("서버 오류가 발생했습니다.");
        }
    };

    /** 목록에서 위아래로 옮긴 순서를 바로 저장한다. */
    const move = async (idx, dir) => {
        const to = idx + dir;
        if (to < 0 || to >= items.length) return;

        const next = [...items];
        [next[idx], next[to]] = [next[to], next[idx]];
        setItems(next); // 응답을 기다리지 않고 먼저 반영해야 연속으로 누를 수 있다

        try {
            const res = await adminFetch("/api/admin/portfolio/reorder", {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(next.map((p) => p.id)),
            });
            if (!res.ok) {
                toast(await readError(res));
                load(); // 실패하면 서버 순서로 되돌린다
            }
        } catch (err) {
            console.error("순서 저장 실패", err);
            toast("순서를 저장하지 못했습니다.");
            load();
        }
    };

    const togglePublished = async (item) => {
        try {
            const res = await adminFetch(`/api/admin/portfolio/${item.id}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ ...item, published: !item.published }),
            });
            if (!res.ok) {
                toast(await readError(res));
                return;
            }
            load();
        } catch (err) {
            console.error("공개 설정 변경 실패", err);
            toast("서버 오류가 발생했습니다.");
        }
    };

    // ---------------------------------------------------------------- 렌더

    if (loading) {
        return (
            <div className="sch-adminPage">
                <AdminSidebar />
                <div className="sch-adminMain">
                    <p className="sc-caption">불러오는 중…</p>
                </div>
            </div>
        );
    }

    return (
        <div className="sch-adminPage">
            <AdminSidebar />

            <div className="sch-adminMain">
                <div className="sch-adminHead">
                    <h2 className="sc-h2">포트폴리오 관리</h2>
                    <button type="button" className="sc-btn sc-btn--sm" onClick={startCreate}>
                        새 사례 등록
                    </button>
                </div>

                {loadError && <div className="sc-alert sc-alert--danger">{loadError}</div>}

                {editing && (
                    <form className="sch-pfform" onSubmit={save}>
                        <h3 className="sc-h3">{editing.id ? "사례 수정" : "새 사례"}</h3>

                        <div className="sc-field">
                            <label className="sc-field__label" htmlFor="pf-title">프로젝트명 *</label>
                            <input id="pf-title" className="sc-input" name="title"
                                   value={editing.title} onChange={onChange} />
                        </div>

                        <div className="sc-field">
                            <label className="sc-field__label" htmlFor="pf-seo">검색용 제목</label>
                            <input id="pf-seo" className="sc-input" name="seoTitle"
                                   value={editing.seoTitle || ""} onChange={onChange}
                                   maxLength={200} />
                            <span className="sc-field__help">
                                검색 결과에만 쓰입니다. 화면 제목은 그대로입니다.
                                사람들이 검색창에 칠 말로 적으세요. 예) 대량 메일 발송 프로그램 제작 사례
                            </span>
                        </div>

                        <div className="sc-field">
                            <label className="sc-field__label" htmlFor="pf-summary">한 줄 요약</label>
                            <input id="pf-summary" className="sc-input" name="summary"
                                   value={editing.summary} onChange={onChange} />
                            <span className="sc-field__help">목록에 노출됩니다.</span>
                        </div>

                        <div className="sch-pfgrid">
                            <div className="sc-field">
                                <label className="sc-field__label" htmlFor="pf-client">발주처</label>
                                <input id="pf-client" className="sc-input" name="client"
                                       value={editing.client} onChange={onChange} />
                                <span className="sc-field__help">공개가 어려우면 "제조업 A사" 처럼 적습니다.</span>
                            </div>
                            <div className="sc-field">
                                <label className="sc-field__label" htmlFor="pf-period">기간</label>
                                <input id="pf-period" className="sc-input" name="period"
                                       value={editing.period} onChange={onChange} placeholder="2026.01 ~ 2026.03" />
                            </div>
                            <div className="sc-field">
                                <label className="sc-field__label" htmlFor="pf-budget">규모</label>
                                <input id="pf-budget" className="sc-input" name="budget"
                                       value={editing.budget} onChange={onChange} />
                                <span className="sc-field__help">비우면 화면에 표시되지 않습니다.</span>
                            </div>
                            <div className="sc-field">
                                <label className="sc-field__label" htmlFor="pf-role">역할</label>
                                <input id="pf-role" className="sc-input" name="role"
                                       value={editing.role} onChange={onChange} />
                            </div>
                            <div className="sc-field">
                                <label className="sc-field__label" htmlFor="pf-category">분류</label>
                                <input id="pf-category" className="sc-input" name="category" list="pf-categories"
                                       value={editing.category} onChange={onChange} />
                                <datalist id="pf-categories">
                                    {categories.map((c) => <option key={c} value={c} />)}
                                </datalist>
                                <span className="sc-field__help">색인 필터에 쓰입니다.</span>
                            </div>
                            <div className="sc-field">
                                <label className="sc-field__label" htmlFor="pf-link">원본 링크</label>
                                <input id="pf-link" className="sc-input" name="link"
                                       value={editing.link} onChange={onChange} placeholder="https://" />
                            </div>
                        </div>

                        <div className="sc-field">
                            <label className="sc-field__label" htmlFor="pf-problem">문제</label>
                            <textarea id="pf-problem" className="sc-textarea" name="problem" rows={4}
                                      value={editing.problem} onChange={onChange} />
                        </div>
                        <div className="sc-field">
                            <label className="sc-field__label" htmlFor="pf-solution">해결</label>
                            <textarea id="pf-solution" className="sc-textarea" name="solution" rows={5}
                                      value={editing.solution} onChange={onChange} />
                        </div>
                        <div className="sc-field">
                            <label className="sc-field__label" htmlFor="pf-result">성과</label>
                            <textarea id="pf-result" className="sc-textarea" name="result" rows={4}
                                      value={editing.result} onChange={onChange} />
                            <span className="sc-field__help">숫자가 있으면 설득력이 크게 올라갑니다.</span>
                        </div>

                        <div className="sc-field">
                            <label className="sc-field__label" htmlFor="pf-stack">기술 스택</label>
                            <input id="pf-stack" className="sc-input"
                                   value={editing.stack.join(", ")}
                                   onChange={(e) => setField("stack", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} />
                            <span className="sc-field__help">쉼표로 구분합니다. 예) Python, MySQL, Selenium</span>
                        </div>

                        <div className="sc-field">
                            <span className="sc-field__label">화면 스크린샷</span>
                            <label className="sc-btn sc-btn--secondary sc-btn--sm sch-pfupload">
                                {uploading ? "올리는 중…" : "이미지 추가"}
                                <input type="file" accept="image/*" multiple hidden disabled={uploading}
                                       onChange={(e) => { uploadImages(e.target.files); e.target.value = ""; }} />
                            </label>

                            {editing.images.length === 0 ? (
                                <span className="sc-field__help">아직 올린 이미지가 없습니다.</span>
                            ) : (
                                <ul className="sch-pfimglist">
                                    {editing.images.map((img, i) => (
                                        <li key={img.src + i} className="sch-pfimg">
                                            <img src={img.src} alt={img.alt || ""} loading="lazy" />
                                            <input
                                                className="sc-input"
                                                value={img.alt || ""}
                                                placeholder="어떤 화면인지 설명"
                                                onChange={(e) => setImageAlt(i, e.target.value)}
                                            />
                                            <div className="sch-pfimgbtns">
                                                <button type="button" className="sc-btn sc-btn--ghost sc-btn--sm"
                                                        onClick={() => moveImage(i, -1)} disabled={i === 0}
                                                        aria-label="앞으로">↑</button>
                                                <button type="button" className="sc-btn sc-btn--ghost sc-btn--sm"
                                                        onClick={() => moveImage(i, 1)}
                                                        disabled={i === editing.images.length - 1}
                                                        aria-label="뒤로">↓</button>
                                                <button type="button" className="sc-btn sc-btn--danger sc-btn--sm"
                                                        onClick={() => removeImage(i)} aria-label="빼기">✕</button>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        <label className="sc-choice">
                            <input type="checkbox" className="sc-check" checked={!!editing.published}
                                   onChange={(e) => setField("published", e.target.checked)} />
                            홈페이지에 공개
                        </label>

                        <div className="sch-pfformbtns">
                            <button type="submit" className="sc-btn" disabled={saving}>
                                {saving ? "저장 중…" : "저장"}
                            </button>
                            <button type="button" className="sc-btn sc-btn--secondary" onClick={closeForm}>
                                취소
                            </button>
                        </div>
                    </form>
                )}

                {items.length === 0 && !loadError ? (
                    <div className="sc-empty">
                        <div className="sc-empty__title">등록된 사례가 없습니다</div>
                        <div className="sc-empty__desc">「새 사례 등록」으로 추가하세요.</div>
                    </div>
                ) : (
                    <div className="sc-tablewrap">
                        <table className="sc-table">
                            <thead>
                            <tr>
                                <th>순서</th>
                                <th>프로젝트명</th>
                                <th>분류</th>
                                <th>이미지</th>
                                <th>공개</th>
                                <th>관리</th>
                            </tr>
                            </thead>
                            <tbody>
                            {items.map((item, i) => (
                                <tr key={item.id}>
                                    <td>
                                        <div className="sch-pfmove">
                                            <button type="button" className="sc-btn sc-btn--ghost sc-btn--sm"
                                                    onClick={() => move(i, -1)} disabled={i === 0}
                                                    aria-label="위로">↑</button>
                                            <button type="button" className="sc-btn sc-btn--ghost sc-btn--sm"
                                                    onClick={() => move(i, 1)} disabled={i === items.length - 1}
                                                    aria-label="아래로">↓</button>
                                        </div>
                                    </td>
                                    <td className="sch-pfname">
                                        {item.title}
                                        {item.summary && <span>{item.summary}</span>}
                                    </td>
                                    <td>{item.category || "—"}</td>
                                    <td>{item.images?.length || 0}장</td>
                                    <td>
                                        <button type="button"
                                                className={`sc-badge ${item.published ? "sc-badge--paid" : "sc-badge--done"}`}
                                                onClick={() => togglePublished(item)}
                                                title="눌러서 공개 여부를 바꿉니다">
                                            {item.published ? "공개" : "숨김"}
                                        </button>
                                    </td>
                                    <td>
                                        <div className="sch-pfrowbtns">
                                            <button type="button" className="sc-btn sc-btn--secondary sc-btn--sm"
                                                    onClick={() => startEdit(item)}>수정</button>
                                            <button type="button" className="sc-btn sc-btn--danger sc-btn--sm"
                                                    onClick={() => setConfirmDelete(item)}>삭제</button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {confirmDelete && (
                <div className="sc-modal-backdrop" onClick={() => setConfirmDelete(null)}>
                    <div className="sc-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="sc-modal__head">
                            <span className="sc-modal__title">사례를 삭제할까요?</span>
                            <button type="button" className="sc-modal__x"
                                    onClick={() => setConfirmDelete(null)} aria-label="닫기">✕</button>
                        </div>
                        <div className="sc-modal__body">
                            <p>「{confirmDelete.title}」와 올려 둔 스크린샷이 함께 지워집니다. 되돌릴 수 없습니다.</p>
                        </div>
                        <div className="sc-modal__foot">
                            <button type="button" className="sc-btn sc-btn--secondary"
                                    onClick={() => setConfirmDelete(null)}>취소</button>
                            <button type="button" className="sc-btn sc-btn--danger"
                                    onClick={() => remove(confirmDelete)}>삭제</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminPortfolioManager;
